import { useEffect, useState } from 'react';
import { apiClient } from '../../auth-lib';
import { Button, Modal, Input, Select, useToast } from '../../design-system';
import { STATUS_OPTIONS } from '../../lib/physicalFiles';

const emptyForm = {
  clientId: '',
  numberMode: 'auto', // 'auto' → system PF-0001 | 'custom' → user types
  fileNumber: '',
  title: '',
  location: '',
  status: 'active',
  remarks: '',
};

// One modal for add and edit. `file` null → create; otherwise edit that row.
// `lockClientId` pre-selects and locks the client (used from the client screen).
function PhysicalFileModal({ file, lockClientId, onClose, onSaved }) {
  const toast = useToast();
  const isEdit = Boolean(file);
  const [form, setForm] = useState(emptyForm);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get('/api/clients').then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (file) {
      setForm({
        ...emptyForm,
        ...file,
        numberMode: 'custom',
        clientId: file.clientId?._id || file.clientId || '',
      });
    } else {
      setForm({ ...emptyForm, clientId: lockClientId || '' });
    }
  }, [file, lockClientId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNumber = (e) => setForm((f) => ({ ...f, fileNumber: e.target.value.toUpperCase() }));

  const useCustom = isEdit || form.numberMode === 'custom';

  const save = async (e) => {
    e?.preventDefault();
    setError('');
    if (!form.clientId) return setError('Select a client');
    if (useCustom && !form.fileNumber.trim()) {
      return setError('Enter a file number, or switch to auto-generate');
    }

    const payload = {
      clientId: form.clientId,
      title: form.title.trim(),
      location: form.location.trim(),
      status: form.status,
      remarks: form.remarks.trim(),
    };
    if (useCustom) payload.fileNumber = form.fileNumber.trim().toUpperCase();

    setSaving(true);
    try {
      if (isEdit) {
        await apiClient.put(`/api/physical-files/${file._id}`, payload);
        toast.success('File updated');
      } else {
        await apiClient.post('/api/physical-files', payload);
        toast.success('File allocated');
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save file');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
      <Button variant="primary" onClick={save} loading={saving}>{isEdit ? 'Save Changes' : 'Allocate File'}</Button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? 'Edit Physical File' : 'Allocate Physical File'} footer={footer} size="lg">
      <form className="pf-form" onSubmit={save}>
        {error && <div className="pf-form-error" role="alert">{error}</div>}

        <Select label="Client *" value={form.clientId} onChange={set('clientId')} disabled={Boolean(lockClientId) && !isEdit}>
          <option value="">— Select client —</option>
          {clients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
        </Select>

        <div className="pf-form-row">
          {isEdit ? (
            <Input label="File Number *" value={form.fileNumber} onChange={setNumber} placeholder="e.g. PF-0001" />
          ) : (
            <Select label="File Number *" value={form.numberMode} onChange={set('numberMode')}>
              <option value="auto">Auto-generate (PF-0001)</option>
              <option value="custom">Custom number</option>
            </Select>
          )}
          <Input label="Title" value={form.title} onChange={set('title')} placeholder="e.g. Permanent File, Vol 1" />
        </div>
        {!isEdit && form.numberMode === 'custom' && (
          <Input label="Custom file number *" value={form.fileNumber} onChange={setNumber} placeholder="e.g. SH/2026/01" />
        )}
        {!isEdit && form.numberMode === 'auto' && (
          <p className="pf-form-hint">A number like <strong>PF-0001</strong> is assigned automatically when you save.</p>
        )}

        <div className="pf-form-row">
          <Input label="Location" value={form.location} onChange={set('location')} placeholder="Rack / shelf / cabinet" />
          <Select label="Status" value={form.status} onChange={set('status')}>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </div>

        <Input label="Remarks" value={form.remarks} onChange={set('remarks')} placeholder="Notes about this file" />
      </form>
    </Modal>
  );
}

export default PhysicalFileModal;
